import { runInAction } from "mobx";
import type {
  AdminDashboardSummary,
  DashboardStats,
  IWarehouseApi,
} from "../../../api/WarehouseApi";
import { isBoxId, isProductId } from "../../../domain/entity-ids";
import {
  type RealtimeFeedItem,
  type IRealtimeStore,
} from "../../../stores/RealtimeStore";
import { type INavigationStore, Screens } from "../../../stores/navigation";
import type { IStore } from "../../../stores/store";
import { clearStoredSession } from "../../../utils/authStorage";
import {
  openBoxScreenById,
  openProductScreenById,
} from "../../shared/use-cases/entity-navigation";
import { isDeletedEntityEvent, mapJournalEntryToFeedItem } from "./events";

export interface MainDashboardOperationState {
  journalEvents: RealtimeFeedItem[];
  isJournalLoading: boolean;
  journalError: string | null;
  stats: DashboardStats;
  isStatsLoading: boolean;
  statsError: string | null;
  adminSummary: AdminDashboardSummary;
  adminSummaryError: string | null;
  isAdminSummaryLoading: boolean;
  isLoggingOut: boolean;
}

export function reconnectDashboardRealtime(input: {
  realtimeStore: IRealtimeStore;
  loadLastOperations: () => Promise<void>;
}): void {
  input.realtimeStore.reconnect();
  void input.loadLastOperations();
}

export async function logoutDashboard(input: {
  state: MainDashboardOperationState;
  api: IWarehouseApi;
  realtimeStore: IRealtimeStore;
  navigationStore: INavigationStore;
}): Promise<void> {
  if (input.state.isLoggingOut) {
    return;
  }

  runInAction(() => {
    input.state.isLoggingOut = true;
  });

  try {
    await input.api.logout();
  } catch {
    clearStoredSession();
  } finally {
    input.realtimeStore.stop();
    input.navigationStore.setScreen(Screens.Login);
    runInAction(() => {
      input.state.isLoggingOut = false;
    });
  }
}

export async function openDashboardEvent(input: {
  state: MainDashboardOperationState;
  event: RealtimeFeedItem;
  api: IWarehouseApi;
  store: IStore;
  navigationStore: INavigationStore;
}): Promise<void> {
  if (isDeletedEntityEvent(input.event)) {
    input.navigationStore.setScreen(Screens.DeletedEntities);
    return;
  }

  const productId = input.event.productId?.trim() || "";
  if (isProductId(productId)) {
    const existingProduct = input.store.products.find(
      (item) => item.id === productId,
    );
    if (existingProduct) {
      input.navigationStore.setScreen(Screens.Product, existingProduct);
      return;
    }

    const openedProduct = await openProductScreenById({
      api: input.api,
      navigationStore: input.navigationStore,
      productId,
      onLoaded: (product) => {
        input.store.addProduct(product);
      },
    });
    if (openedProduct) {
      return;
    }
  }

  const boxId = input.event.boxId?.trim() || "";
  if (isBoxId(boxId)) {
    const existingBox = input.store.boxes.find((item) => item.id === boxId);
    if (existingBox) {
      input.navigationStore.setScreen(Screens.Box, existingBox);
      return;
    }

    const openedBox = await openBoxScreenById({
      api: input.api,
      navigationStore: input.navigationStore,
      boxId,
      onLoaded: (box) => {
        input.store.addBox(box);
      },
    });
    if (openedBox) {
      return;
    }
  }

  input.state.journalError =
    "Не удалось определить продукт или коробку для выбранного события";
}

export async function refreshDashboardData(input: {
  loadLastOperations: () => Promise<void>;
  loadStats: () => Promise<void>;
  loadAdminSummary: () => Promise<void>;
}): Promise<void> {
  await Promise.all([
    input.loadLastOperations(),
    input.loadStats(),
    input.loadAdminSummary(),
  ]);
}

export async function loadLastOperationsData(input: {
  state: MainDashboardOperationState;
  api: IWarehouseApi;
  limit: number;
}): Promise<void> {
  runInAction(() => {
    input.state.isJournalLoading = true;
    input.state.journalError = null;
  });

  try {
    const entries = await input.api.getRecentJournalFeed(input.limit);
    runInAction(() => {
      input.state.journalEvents = entries.map((entry) =>
        mapJournalEntryToFeedItem(entry),
      );
    });
  } catch (error) {
    runInAction(() => {
      input.state.journalError =
        error instanceof Error
          ? error.message
          : "Не удалось загрузить последние операции";
    });
  } finally {
    runInAction(() => {
      input.state.isJournalLoading = false;
    });
  }
}

export async function loadWarehouseStatsData(input: {
  state: MainDashboardOperationState;
  api: IWarehouseApi;
  expiringDays: number;
}): Promise<void> {
  runInAction(() => {
    input.state.isStatsLoading = true;
    input.state.statsError = null;
  });

  try {
    const stats = await input.api.getDashboardStats(input.expiringDays);
    runInAction(() => {
      input.state.stats = stats;
    });
  } catch (error) {
    runInAction(() => {
      input.state.statsError =
        error instanceof Error
          ? error.message
          : "Не удалось загрузить статистику склада";
    });
  } finally {
    runInAction(() => {
      input.state.isStatsLoading = false;
    });
  }
}

export async function loadAdminSummaryData(input: {
  state: MainDashboardOperationState;
  api: IWarehouseApi;
  isAdmin: boolean;
  emptyAdminSummary: AdminDashboardSummary;
}): Promise<void> {
  if (!input.isAdmin) {
    runInAction(() => {
      input.state.adminSummary = input.emptyAdminSummary;
      input.state.adminSummaryError = null;
    });
    return;
  }

  runInAction(() => {
    input.state.isAdminSummaryLoading = true;
    input.state.adminSummaryError = null;
  });

  try {
    const summary = await input.api.getAdminDashboardSummary();
    runInAction(() => {
      input.state.adminSummary = summary;
    });
  } catch (error) {
    runInAction(() => {
      input.state.adminSummaryError =
        error instanceof Error
          ? error.message
          : "Не удалось загрузить admin-статистику";
    });
  } finally {
    runInAction(() => {
      input.state.isAdminSummaryLoading = false;
    });
  }
}
